/**
 * OpenTelemetry configuration for React frontend
 * Supports both local development and Grafana Cloud for multiple environments
 */

import { Resource } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { trace } from '@opentelemetry/api';

// Configuration
const getEnvironment = () => {
  return process.env.REACT_APP_ENVIRONMENT || 'dev';
};

const getServiceName = () => {
  const env = getEnvironment();
  return `cosigned-frontend-${env}`;
};

const getBackendUrl = () => {
  return process.env.REACT_APP_API_SERVER_URL || 'http://localhost:8000';
};

let isInitialized = false;

/**
 * Get the appropriate trace exporter based on environment and configuration
 */
function getTraceExporter() {
  const env = getEnvironment();
  
  // For non-dev environments, use backend proxy to avoid CORS issues
  if (env !== 'dev') {
    const backendUrl = getBackendUrl();
    const proxyEndpoint = `${backendUrl}/api/telemetry/traces/`;
    console.log(`Using backend proxy for traces in ${env} environment: ${proxyEndpoint}`);
    
    return new OTLPTraceExporter({
      url: proxyEndpoint,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
  
  // For development, try to use local OTLP endpoint or configured endpoint
  const otlpEndpoint = process.env.REACT_APP_OTEL_EXPORTER_OTLP_TRACES_ENDPOINT;
  if (otlpEndpoint) {
    console.log(`Using OTLP endpoint for traces in ${env} environment: ${otlpEndpoint}`);
    
    const headers = {};
    const otlpHeaders = process.env.REACT_APP_OTEL_EXPORTER_OTLP_HEADERS;
    if (otlpHeaders) {
      // Parse headers from environment variable (format: key=value,key2=value2)
      otlpHeaders.split(',').forEach(header => {
        const equalIndex = header.indexOf('=');
        if (equalIndex > 0) {
          const key = header.substring(0, equalIndex).trim();
          const value = decodeURIComponent(header.substring(equalIndex + 1).trim());
          headers[key] = value;
        }
      });
    }
    
    return new OTLPTraceExporter({
      url: otlpEndpoint,
      headers,
    });
  }
  
  // Fallback for development - use local OTLP endpoint
  if (env === 'dev') {
    console.log(`Using local OTLP endpoint for development`);
    return new OTLPTraceExporter({
      url: 'http://localhost:4318/v1/traces', // Local OTLP HTTP endpoint
    });
  }
  
  console.warn(`No trace exporter configured for ${env} environment`);
  return null;
}

/**
 * Initialize OpenTelemetry for the React application
 */
export function initializeOpenTelemetry() {
  if (isInitialized) {
    console.warn('OpenTelemetry already initialized');
    return;
  }

  const env = getEnvironment();
  
  // Skip telemetry initialization in development to avoid CORS issues
  if (env === 'dev') {
    console.log('Skipping OpenTelemetry initialization in development environment');
    isInitialized = true;
    return;
  }

  // Frontend OpenTelemetry now uses backend proxy for staging/production to avoid CORS issues

  try {
    const serviceName = getServiceName();
    const backendUrl = getBackendUrl();
    
    // Create resource with service information
    const resource = new Resource({
      [SemanticResourceAttributes.SERVICE_NAME]: serviceName,
      [SemanticResourceAttributes.SERVICE_VERSION]: process.env.REACT_APP_SERVICE_VERSION || '1.0.0',
      [SemanticResourceAttributes.SERVICE_INSTANCE_ID]: window.location.hostname,
      [SemanticResourceAttributes.DEPLOYMENT_ENVIRONMENT]: env,
      [SemanticResourceAttributes.SERVICE_NAMESPACE]: 'cosigned',
    });

    // Create tracer provider
    const provider = new WebTracerProvider({
      resource: resource,
    });

    // Get the appropriate exporter
    const exporter = getTraceExporter();
    
    if (exporter) {
      // Add span processor
      provider.addSpanProcessor(new BatchSpanProcessor(exporter));
    }

    // Register the provider
    provider.register();

    // Note: Auto-instrumentation removed to avoid Node.js polyfill issues
    // Manual tracing will be used via helper functions

    isInitialized = true;
    console.log(`OpenTelemetry initialized successfully for ${serviceName} in ${env} environment`);
  } catch (error) {
    console.error('Failed to initialize OpenTelemetry:', error);
  }
}

/**
 * Get the current tracer instance
 */
export function getTracer() {
  const serviceName = getServiceName();
  const serviceVersion = process.env.REACT_APP_SERVICE_VERSION || '1.0.0';
  return trace.getTracer(serviceName, serviceVersion);
}

/**
 * Create a custom span for manual instrumentation
 */
export function createSpan(name, attributes = {}) {
  const env = getEnvironment();
  
  // Return a no-op span for development
  if (env === 'dev') {
    return {
      setAttributes: () => {},
      setStatus: () => {},
      recordException: () => {},
      end: () => {},
    };
  }
  
  const tracer = getTracer();
  
  return tracer.startSpan(name, {
    attributes: {
      ...attributes,
      'service.name': getServiceName(),
      'deployment.environment': env,
      'service.namespace': 'cosigned',
    },
  });
}

/**
 * Helper function to trace async operations
 */
export async function traceAsyncOperation(name, operation, attributes = {}) {
  const span = createSpan(name, attributes);
  
  try {
    const result = await operation();
    span.setStatus({ code: 1 }); // OK
    return result;
  } catch (error) {
    span.setStatus({ code: 2, message: error.message }); // ERROR
    span.recordException(error);
    throw error;
  } finally {
    span.end();
  }
}

/**
 * Helper function to add user context to spans
 */
export function addUserContext(span, user) {
  if (user && span) {
    span.setAttributes({
      'user.id': user.sub || user.id,
      'user.email': user.email,
      'user.name': user.name,
    });
  }
}

/**
 * Helper function to add route context to spans
 */
export function addRouteContext(span, route) {
  if (route && span) {
    span.setAttributes({
      'route.path': route.pathname,
      'route.search': route.search,
      'route.hash': route.hash,
    });
  }
}

/**
 * Helper function to add environment-specific attributes to spans
 */
export function addEnvironmentAttributes(span) {
  if (span) {
    const env = getEnvironment();
    span.setAttributes({
      'deployment.environment': env,
      'service.namespace': 'cosigned',
      'environment.type': env === 'production' ? 'production' : 
                         env === 'staging' ? 'staging' : 'development',
    });
  }
}

const OTEL_ATTR_MAX = 256;

/**
 * Flatten a plain object into OTEL-safe primitive attributes.
 * Nested objects/arrays are JSON-stringified and truncated.
 */
export function flattenOmicsAttributes(data = {}, prefix = 'omics') {
  const out = {};
  if (!data || typeof data !== 'object') {
    return out;
  }
  Object.entries(data).forEach(([key, value]) => {
    if (value == null) return;
    const attrKey = `${prefix}.${String(key).replace(/[^a-zA-Z0-9_.-]/g, '_')}`;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[attrKey] = typeof value === 'string' && value.length > OTEL_ATTR_MAX
        ? `${value.slice(0, OTEL_ATTR_MAX)}…`
        : value;
      return;
    }
    try {
      const encoded = JSON.stringify(value);
      out[attrKey] = encoded.length > OTEL_ATTR_MAX
        ? `${encoded.slice(0, OTEL_ATTR_MAX)}…`
        : encoded;
    } catch (_) {
      out[attrKey] = String(value).slice(0, OTEL_ATTR_MAX);
    }
  });
  return out;
}

/**
 * Emit a short-lived BioChat span for Grafana Tempo (staging/prod).
 * No-ops in local dev where OpenTelemetry init is skipped.
 */
export function recordOmicsEvent(name, attributes = {}) {
  const eventName = String(name || 'event').slice(0, 120);
  const span = createSpan(`omics.${eventName}`, flattenOmicsAttributes(attributes));
  try {
    span.setStatus({ code: 1 });
  } finally {
    span.end();
  }
}

/**
 * Helper function to trace fetch/API calls manually
 */
export async function traceFetch(url, options = {}, spanName = null) {
  const name = spanName || `HTTP ${options.method || 'GET'} ${url}`;
  const span = createSpan(name, {
    'http.url': url,
    'http.method': options.method || 'GET',
  });
  
  try {
    const response = await fetch(url, options);
    span.setAttributes({
      'http.status_code': response.status,
      'http.response.status_code': response.status,
    });
    
    if (response.status >= 400) {
      span.setStatus({ code: 2, message: `HTTP ${response.status}` });
    } else {
      span.setStatus({ code: 1 }); // OK
    }
    
    return response;
  } catch (error) {
    span.setStatus({ code: 2, message: error.message });
    span.recordException(error);
    throw error;
  } finally {
    span.end();
  }
} 